/**
 * GeofenceMapPicker (ADR-0014 Phase 6 follow-up) — pick a geofence centre on a
 * map instead of typing raw coordinates. Leaflet + OpenStreetMap tiles (no API
 * key), with a Nominatim address search so a REMOTE office can be located without
 * being there. Click the map or drag the pin to set the centre; the radius circle
 * previews the fence. Writes lat/long back to the form via `onChange`.
 *
 * A `divIcon` (CSS pin) avoids Leaflet's broken default marker-image URLs under
 * bundlers. Tiles/geocoding are fetched at runtime from *.openstreetmap.org
 * (needs network + CSP allowance; OSM attribution shown).
 */
import { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const round = (n: number): number => Number(n.toFixed(7));

const PIN = L.divIcon({
  className: '',
  html: '<div style="width:14px;height:14px;border-radius:50%;background:#facc15;border:2px solid #1f2937;box-shadow:0 0 0 2px #facc15"></div>',
  iconSize: [14, 14],
  iconAnchor: [7, 7],
});

interface Props {
  latitude: number;
  longitude: number;
  radiusMeters: number;
  onChange: (lat: number, lng: number) => void;
}

export function GeofenceMapPicker({ latitude, longitude, radiusMeters, onChange }: Props): JSX.Element {
  const mapEl = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const markerRef = useRef<L.Marker | null>(null);
  const circleRef = useRef<L.Circle | null>(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const [search, setSearch] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);

  // Init the map once.
  useEffect(() => {
    if (!mapEl.current || mapRef.current) return;
    const hasPoint = Number.isFinite(latitude) && Number.isFinite(longitude) && (latitude !== 0 || longitude !== 0);
    const start: L.LatLngExpression = [hasPoint ? latitude : 6.5244, hasPoint ? longitude : 3.3792];
    const map = L.map(mapEl.current).setView(start, hasPoint ? 15 : 11);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { attribution: '© OpenStreetMap contributors', maxZoom: 19 }).addTo(map);
    const marker = L.marker(start, { draggable: true, icon: PIN }).addTo(map);
    const circle = L.circle(start, { radius: Number.isFinite(radiusMeters) && radiusMeters > 0 ? radiusMeters : 100, color: '#facc15', weight: 1, fillOpacity: 0.1 }).addTo(map);
    const place = (lat: number, lng: number): void => {
      marker.setLatLng([lat, lng]);
      circle.setLatLng([lat, lng]);
      onChangeRef.current(round(lat), round(lng));
    };
    marker.on('dragend', () => { const p = marker.getLatLng(); place(p.lat, p.lng); });
    map.on('click', (e: L.LeafletMouseEvent) => place(e.latlng.lat, e.latlng.lng));
    mapRef.current = map;
    markerRef.current = marker;
    circleRef.current = circle;
    return () => { map.remove(); mapRef.current = null; markerRef.current = null; circleRef.current = null; };
  }, []);

  // Reflect externally-set coordinates (manual typing / use-my-location / search).
  useEffect(() => {
    if (!mapRef.current || !markerRef.current || !circleRef.current) return;
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return;
    const p: L.LatLngExpression = [latitude, longitude];
    markerRef.current.setLatLng(p);
    circleRef.current.setLatLng(p);
    mapRef.current.setView(p);
  }, [latitude, longitude]);

  // Reflect radius changes.
  useEffect(() => {
    if (circleRef.current && Number.isFinite(radiusMeters) && radiusMeters > 0) circleRef.current.setRadius(radiusMeters);
  }, [radiusMeters]);

  async function doSearch(): Promise<void> {
    const q = search.trim();
    if (!q) return;
    setSearching(true);
    setSearchError(null);
    try {
      const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(q)}`, { headers: { Accept: 'application/json' } });
      const results = (await res.json()) as Array<{ lat: string; lon: string }>;
      if (!results.length) { setSearchError('No match for that address.'); return; }
      onChangeRef.current(round(Number(results[0]!.lat)), round(Number(results[0]!.lon)));
    } catch {
      setSearchError('Address lookup failed.');
    } finally {
      setSearching(false);
    }
  }

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void doSearch(); } }}
          placeholder="Search an address (e.g. a remote office)…"
          aria-label="Search address"
          className="flex-1 rounded border border-gray-700 bg-gray-900 px-3 py-2 text-white text-sm"
        />
        <button type="button" onClick={() => void doSearch()} disabled={searching} className="rounded border border-gray-700 text-sm px-4 py-2 hover:bg-gray-900 disabled:opacity-50">
          {searching ? 'Searching…' : 'Search'}
        </button>
      </div>
      {searchError ? <p className="text-xs text-red-400">{searchError}</p> : null}
      <div ref={mapEl} aria-label="Geofence map" style={{ height: 280 }} className="rounded border border-gray-800 overflow-hidden" />
      <p className="text-[0.65rem] text-gray-600">Click the map or drag the pin to set the centre. Tiles © OpenStreetMap contributors.</p>
    </div>
  );
}
